import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
  IconButton,
  Skeleton,
  Container,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  Fab,
  Zoom,
  Avatar,
} from '@mui/material';
import {
  Search,
  Refresh,
  ViewModule,
  ViewList,
  FilterList,
  Launch,
  Assignment,
  Person,
  AccountCircle,
  Face,
  SentimentSatisfied,
  Pets,
  Android,
  BugReport,
  Cake,
  EmojiEmotions,
  SportsEsports,
} from '@mui/icons-material';
import { useNFT, NFTItem } from '../hooks/useNFT';
import { getContract } from '../config/contracts';
import { useWeb3React } from '@web3-react/core';
import PermissionGuard from '../components/PermissionGuard';


const AssetsListPage: React.FC = () => {
  return  <PermissionGuard requireConnection><AssetsListContent /></PermissionGuard>;
};

const AssetsListContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { chainId, provider } = useWeb3React();
  const [contractAddress, setContractAddress] = useState<string>('');
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [refreshing, setRefreshing] = useState(false);
  const itemsPerPage = isMobile ? 8 : 12;

  useEffect(() => {
    if (chainId && provider) {
      try {
        const contract = getContract(chainId, provider);
        setContractAddress(contract.address);
      } catch (err) {
        console.error('Failed to get contract address:', err);
      }
    }
  }, [chainId, provider]);

  const { nfts, isLoading, error, loadNFTs, refreshNFTs } = useNFT(contractAddress || undefined);

  useEffect(() => {
    if (contractAddress && contractAddress.length > 0) {
      loadNFTs(undefined, itemsPerPage);
    }
  }, [contractAddress, loadNFTs, itemsPerPage]);

  const handleRefresh = async () => {
    setPage(1);
    setRefreshing(true);
    try {
      await refreshNFTs();
    } finally {
      setRefreshing(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const filteredNFTs = nfts.filter(nft => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      nft.tokenId.toLowerCase().includes(term) ||
      nft.metadata?.name?.toLowerCase().includes(term) ||
      nft.owner.toLowerCase().includes(term)
    );
  });

  const paginatedNFTs = filteredNFTs.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(filteredNFTs.length / itemsPerPage);

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Avatar icon array
  const avatarIcons = [
    Person,
    AccountCircle,
    Face,
    SentimentSatisfied,
    Pets,
    Android,
    BugReport,
    Cake,
    EmojiEmotions,
    SportsEsports,
  ];

  // Generate a random avatar based on tokenId
  const getRandomAvatar = (tokenId: string) => {
    const hash = tokenId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const iconIndex = hash % avatarIcons.length;
    const IconComponent = avatarIcons[iconIndex];
    
    // Generate random color
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'
    ];
    const colorIndex = (hash * 7) % colors.length;
    const backgroundColor = colors[colorIndex];
    
    return { IconComponent, backgroundColor };
  };

  const NFTCardSkeleton = () => (
    <Card sx={{ 
      height: 'auto',
      minHeight: { xs: 120, sm: 140 },
      borderRadius: 2,
      boxShadow: 1
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton 
            variant="circular" 
            width={isSmallScreen ? 48 : 56} 
            height={isSmallScreen ? 48 : 56} 
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Skeleton variant="rectangular" width={50} height={20} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
          <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      </CardContent>
    </Card>
  );

  const NFTTableSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Box key={index} sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 2, 
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
          <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
      {/* Page title and action bar */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography 
              variant={isSmallScreen ? "h5" : "h4"}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold',
              }}
            >
             User KYC
            </Typography>
            <Typography 
              variant={isSmallScreen ? "body2" : "body1"} 
              color="text.secondary"
            >
             
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isSmallScreen && (
              <>
                <Tooltip title={viewMode === 'grid' ? 'List View' : 'Grid View'}>
                  <IconButton
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Filter">
                  <IconButton
                    sx={{
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                      },
                    }}
                  >
                    <FilterList />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by NFT ID, name, or owner address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ 
              flexGrow: 1, 
              minWidth: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            size={isSmallScreen ? "small" : "medium"}
          />
          <Button
            variant="outlined"
            startIcon={<Refresh sx={{
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }} />}
            onClick={handleRefresh}
            disabled={isLoading || refreshing}
            size={isSmallScreen ? "small" : "medium"}
            sx={{
              minWidth: { xs: '100%', sm: 'auto' },
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              '&:hover': {
                backgroundColor: alpha(theme.palette.success.main, 0.2),
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
          }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* NFT display area */}
      {isLoading && nfts.length === 0 ? (
        viewMode === 'grid' ? (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {Array.from({ length: itemsPerPage }).map((_, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                lg={3} 
                key={index}
              >
                <NFTCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card sx={{ borderRadius: 2 }}>
            <NFTTableSkeleton />
          </Card>
        )
      ) : filteredNFTs.length > 0 ? (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {paginatedNFTs.map((nft) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  lg={3} 
                  key={nft.tokenId}
                >
                  <Card
                    sx={{
                      height: 'auto',
                      minHeight: { xs: 120, sm: 140 },
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: theme.transitions.create(['transform', 'box-shadow'], {
                        duration: theme.transitions.duration.short,
                      }),
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {(() => {
                          const { IconComponent, backgroundColor } = getRandomAvatar(nft.tokenId);
                          return (
                            <Avatar
                              sx={{
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                                backgroundColor,
                                color: 'white',
                                flexShrink: 0,
                              }}
                            >
                              <IconComponent fontSize={isSmallScreen ? 'medium' : 'large'} />
                            </Avatar>
                          );
                        })()}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant={isSmallScreen ? "subtitle2" : "subtitle1"} 
                            component="h3" 
                            noWrap
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {nft.metadata?.name || `NFT #${nft.tokenId}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: 'block', mb: 1 }}
                          >
                            Owner: {formatAddress(nft.owner)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={`ID: ${nft.tokenId}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                            <Chip
                              label="NFT"
                              size="small"
                              color="primary"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Button
                            component={Link}
                            to={`/assets/${nft.tokenId}`}
                            size="small"
                            variant="contained"
                            sx={{ 
                              fontSize: '0.75rem',
                              minWidth: { xs: 60, sm: 80 },
                              px: 1
                            }}
                          >
                            Details
                          </Button>
                          <Button
                            component={Link}
                            to={`/assets-kyc/${nft.tokenId}`}
                            size="small"
                            variant="outlined"
                            startIcon={<Assignment />}
                            sx={{ fontSize: '0.75rem' }}
                          >
                            KYC Review
                          </Button>
                          <Tooltip title="View owner on blockchain explorer">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const isTestnet = chainId === 80002; // Polygon Amoy testnet
                                const baseUrl = isTestnet ? 'https://amoy.polygonscan.com' : 'https://polygonscan.com';
                                window.open(`${baseUrl}/address/${nft.owner}`, '_blank');
                              }}
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                },
                              }}
                            >
                              <Launch fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {!isSmallScreen ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ backgroundColor: 'grey.50' }}>
                        <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                          NFT
                        </Box>
                        <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                          Name
                        </Box>
                        <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                          Token ID
                        </Box>
                        <Box component="th" sx={{ p: 2, textAlign: 'left', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                          Owner
                        </Box>
                        <Box component="th" sx={{ p: 2, textAlign: 'center', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
                          Actions
                        </Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {paginatedNFTs.map((nft) => (
                        <Box 
                          component="tr" 
                          key={nft.tokenId}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'grey.50',
                            },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            {(() => {
                              const { IconComponent, backgroundColor } = getRandomAvatar(nft.tokenId);
                              return (
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor,
                                    color: 'white',
                                  }}
                                >
                                  <IconComponent fontSize='small' />
                                </Avatar>
                              );
                            })()}
                          </Box>
                          <Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {nft.metadata?.name || `NFT #${nft.tokenId}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {nft.metadata?.description ? 
                                (nft.metadata.description.length > 50 ? 
                                  `${nft.metadata.description.slice(0, 50)}...` : 
                                  nft.metadata.description
                                ) : 'No description available'
                              }
                            </Typography>
                          </Box>
                          <Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Chip
                              label={nft.tokenId}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </Box>
                          <Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Chip
                              label={formatAddress(nft.owner)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </Box>
                          <Box component="td" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                component={Link}
                                to={`/assets/${nft.tokenId}`}
                                size="small"
                                variant="contained"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                View Details
                              </Button>
                              <Button
                                component={Link}
                                to={`/assets-kyc/${nft.tokenId}`}
                                size="small"
                                variant="outlined"
                                startIcon={<Assignment />}
                                sx={{ fontSize: '0.75rem' }}
                              >
                                KYC Review
                              </Button>
                              <Tooltip title="View owner on blockchain explorer">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const isTestnet = chainId === 80002; // Polygon Amoy testnet
                                    const baseUrl = isTestnet ? 'https://amoy.polygonscan.com' : 'https://polygonscan.com';
                                    window.open(`${baseUrl}/address/${nft.owner}`, '_blank');
                                  }}
                                  sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                  }}
                                >
                                  <Launch fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  {paginatedNFTs.map((nft) => (
                    <Box 
                      key={nft.tokenId}
                      sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {(() => {
                           const { IconComponent, backgroundColor } = getRandomAvatar(nft.tokenId);
                           return (
                             <Avatar
                               sx={{
                                 width: 40,
                                 height: 40,
                                 backgroundColor,
                                 color: 'white',
                                 flexShrink: 0,
                               }}
                             >
                               <IconComponent fontSize='small' />
                             </Avatar>
                           );
                         })()}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                            {nft.metadata?.name || `NFT #${nft.tokenId}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={`ID: ${nft.tokenId}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                            <Chip
                              label={formatAddress(nft.owner)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Button
                            component={Link}
                            to={`/assets/${nft.tokenId}`}
                            size="small"
                            variant="contained"
                            sx={{ fontSize: '0.7rem', minWidth: 60 }}
                          >
                            Details
                          </Button>
                          <Button
                            component={Link}
                            to={`/assets-kyc/${nft.tokenId}`}
                            size="small"
                            variant="outlined"
                            startIcon={<Assignment />}
                            sx={{ fontSize: '0.7rem', minWidth: 60 }}
                          >
                            KYC Review
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const isTestnet = chainId === 80002; // Polygon Amoy testnet
                              const baseUrl = isTestnet ? 'https://amoy.polygonscan.com' : 'https://polygonscan.com';
                              window.open(`${baseUrl}/address/${nft.owner}`, '_blank');
                            }}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <Launch fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          )}

           {totalPages > 1 && (
             <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
               <Pagination
                 count={totalPages}
                 page={page}
                 onChange={handlePageChange}
                 color="primary"
                 size={isMobile ? "medium" : "large"}
               />
             </Box>
           )}
         </>
       ) : (
         <Box sx={{ textAlign: 'center', py: 8 }}>
           <Typography variant="h6" color="text.secondary" gutterBottom>
             {searchTerm ? '未找到匹配的NFT' : '暂无NFT数据'}
           </Typography>
           <Typography variant="body2" color="text.secondary">
             {searchTerm ? '请尝试其他搜索条件' : '请连接钱包或等待数据加载'}
           </Typography>
           {searchTerm && (
             <Button
               variant="outlined"
               onClick={() => setSearchTerm('')}
               sx={{ mt: 2 }}
             >
               清除搜索
             </Button>
           )}
         </Box>
       )}
     </Container>
   );
};

export default AssetsListPage;